import React, { useState, useRef, useEffect, useCallback } from "react"
import { Table, Button, Input, Modal } from "antd"
import { SearchOutlined, PhoneOutlined, MailOutlined } from "@ant-design/icons"
import SmartCardList from "../SmartCard/SmartCardList"
import MapView from "../../MapView"
import { useUser } from "../../../contexts/UserContext"
import { fetchUserCards, fetchCardStates } from "../../../api/Cards"
import { getStatusAggregate, mapStatus, getStatusIcon } from "../../../utils/mappers"
import "./AdminDashboard.css"
import { useCards } from "../../../contexts/CardsContext"
import NewCardModal from "./Modals/NewCardModal"
import CardManageModal from "./Modals/CardManageModal"
import { useTranslation } from "react-i18next"

const AdminDashboard = () => {
    const [data, setData] = useState(null)
    const [searchText, setSearchText] = useState("")
    const mapRef = useRef(null)
    const [isMapModalVisible, setIsMapModalVisible] = useState(false)
    const [expandedRowKey, setExpandedRowKey] = useState(null)
    const [clients, setClients] = useState([])
    const { user, getToken } = useUser()
    const { setCards } = useCards()
    const [showNewCard, setShowNewCard] = useState(false)
    const [showModifyCard, setShowModifyCard] = useState(false)
    const selectedUser = useRef(-1)
    const selectedCard = useRef(null)
    const { t } = useTranslation()

    const [states, setStates] = useState([])

    const columns = [
        {
            title: "",
            dataIndex: "status",
            key: "status",
            width: "3rem",
            render: (status) => getStatusIcon(status)
        },
        {
            title: t('Namn'),
            dataIndex: "name",
            key: "name",
            sorter: (a, b) => a.name && b.name && a.name.localeCompare(b.name)
        },
        ...((window.screen.width >= 768)
            ?
            [
                {
                    title: t('page.editaccount.phone'),
                    dataIndex: "phone",
                    key: "phone",
                    sorter: (a, b) => a.phone && b.phone && a.phone.localeCompare(b.phone),
                    render: (phone) => (<a href={`tel:${phone}`}>{phone}</a>)
                },
                {
                    title: t('page.editaccount.email'),
                    dataIndex: "email",
                    key: "email",
                    sorter: (a, b) => a.email && b.email && a.email.localeCompare(b.email),
                    render: (email) => (<a href={`mailto:${email}`} target="_blank" >{email}</a>)
                },
                {
                    title: t('page.editaccount.adress'),
                    dataIndex: "address",
                    key: "address",
                    sorter: (a, b) => a.address && b.address && a.address.localeCompare(b.address)
                },
                {
                    title: t('Reseller'),
                    dataIndex: "reseller",
                    key: "reseller",
                    sorter: (a, b) => a.reseller && b.reseller && a.reseller.localeCompare(b.reseller),
                    render: (_, record) => (record.reseller ? record.reseller : "-")
                }
            ]
            :
            [
                {
                    title: t('Contact'),
                    dataIndex: "contact",
                    key: "contact",
                    width: "8rem",
                    render: ({ phone, email }) => (
                        <div className="row-contact">
                            {phone && (
                                <a href={`tel:${phone}`} aria-label={t('Telefon')}>
                                    <PhoneOutlined />
                                </a>
                            )}
                            {email && (
                                <a href={`mailto:${email}`} aria-label={t('page.editaccount.email')}>
                                    <MailOutlined />
                                </a>
                            )}
                        </div>
                    )
                },
                {
                    title: t('Address'),
                    dataIndex: "address",
                    key: "address",
                    sorter: (a, b) => a.address && b.address && a.address.localeCompare(b.address)
                },
            ])
        /* 
         {
             title: "Anteckningar",
             dataIndex: "notes",
             key: "notes",
             render: (_, record) => (
                 <Button icon={<EditOutlined />} onClick={() => handleNoteClick(record)}>
                     Anteckningar
                 </Button>
             )
         }
        */
    ]


    const loadCards = async () => {
        const token = getToken()
        if (!token) return
        try {
            const inData = await fetchUserCards(token)
            if (typeof inData == "number") {

                return
            }

            setData(inData.data)
            setCards(inData.data.map(({ Cards }) => Cards))
        } catch (err) {
            console.error("Error fetching cards", err)
        }
    }

    useEffect(() => {
        

        
        loadCards()

        const ref = setInterval(() => {
            loadStates();
        }, 1000);

        return () => clearInterval(ref);
        
    }, [])

    useEffect(() => {

        if (states.length == 0)
            return


        if (!data)
            return
        

        const getStatus = (findCardIndex) => {
            return states.find(({ CardIndex, Status }) => CardIndex === findCardIndex)?.Status ?? undefined
        }

        const newData = data.map(({ User, Cards }) => ({
            User: User,
            Cards: Cards.map((card) => ({
                ...card, Status: getStatus(card.CardIndex) ?? 3 //If the card does not exist in the state array, it is offline
            }))
        }))
        setData(newData)
        setCards(newData.map(({ Cards }) => Cards))
    }, [states])

    function newCard() {
        setShowNewCard(true)
    }
    function hasStatesChanged(cmpStates) {
        //console.log(cmpStates)
        if (cmpStates.length !== states.length)
            return true

        cmpStates.forEach( ({ cardIndex, status }) => {
            if(states.find(({ inCardIndex, inStatus }) => inCardIndex == cardIndex && inStatus === status) == undefined)
                return true
        })

        return false
    }

    const loadStates = async () => {
        try {
            const newStates = (await fetchCardStates(getToken())) //.NET is serializing the array as an object with an inner array
            if (typeof newStates == "number") {

                return
            }
            setStates(newStates)
        }
        catch (err) {
            console.error("Error fetching states", err)
        }
    }


    const filteredClients = clients.filter((client) =>
        Object.values(client).some((value) => String(value).toLowerCase().includes(searchText.toLowerCase()))
    )

    const toggleMapModal = () => {
        setIsMapModalVisible(!isMapModalVisible)
        setTimeout(() => {
            if (mapRef.current) {
                mapRef.current.invalidateSize()
            }
        }, 300)
    }

    const onManageCard = (targetCard) => {
        selectedCard.current = targetCard;
        setShowModifyCard(true);
    }

    const expandedRowRender = ({ id }) => {
        const cards = data
            .filter(({ User: { UserID } }) => id === UserID)
            .map(({ Cards }) =>
                Cards.map((card) => ({
                    id: card.CardId,
                    cardIndex: card.CardIndex,
                    cardName: card.CardName,
                    modelName: card.ModelName,
                    status: card.Status,
                    serial: card.SerialNo,
                    hwmodel: card.Hwmodel,
                    hwver: card.Hwver,
                    swver: card.Swver
                }))
            )
            .flat()
        selectedUser.current = id;
        return expandedRowKey === id ? <SmartCardList onManageCard={onManageCard} newCard={newCard} smartCards={cards} inModal={false} /> : null
    }

    useEffect(() => {
        if (!data) return

        const users = data.map(({ User, Cards }) => ({
            status: mapStatus(getStatusAggregate(Cards)),
            id: User.UserID,
            address: User.Address,
            reseller: User.ResellerName,
            email: User.EMail,
            phone: User.Phone,
            name: `${User.FirstName} ${User.LastName}`,
            contact: {
                email: User.EMail ?? "",
                phone: User.Phone ?? ""
            }
        }))
        setClients(users)
        if (users.length == 1) {
            setExpandedRowKey(users[0].id)
        }

        
    }, [data])

    const tableLocale = {
        triggerDesc: t('page.sort.falling'),
        triggerAsc: t('page.sort.rising'),
        cancelSort: t('page.sort.none')
    }

    return (
        <div className="admin-dashboard">
            {showNewCard && selectedUser.current != -1 && (<NewCardModal selectedUser={expandedRowKey} onClose={() => { setShowNewCard(false); loadCards() }}></NewCardModal>)}
            {showModifyCard && selectedCard.current != null && (<CardManageModal selectedCard={selectedCard.current} onClose={() => { setShowModifyCard(false); loadCards(); } }></CardManageModal>)}
            <Input
                className="search-input"
                placeholder={t('page.admindash.search')}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ marginBottom: 16, width: "50%" }}
                prefix={<SearchOutlined />}
            />
            {user.roleId > 0 && (<Button
                onClick={toggleMapModal}
                className="map-button"
                style={{ marginBottom: 16, position: "absolute", right: 15 }}
                type="primary"
            >
                {t('page.admindash.showmap')}
            </Button>)}
            {data && (
                <Table
                    className="admin-dashboard-table"
                    rowKey="id"
                    columns={columns}
                    dataSource={filteredClients}
                    scroll={{ x: "60vw" }}
                    pagination={false}
                    expandable={{
                        expandedRowRender,
                        expandRowByClick: true,
                        showExpandColumn: false,
                        expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
                        onExpand: (expanded, { id }) => setExpandedRowKey(expanded ? id : null)
                    }}
                    locale={tableLocale}
                />
            )}
            <Modal
                className="map-modal"
                title={t('page.admindash.mapview')}
                open={isMapModalVisible}
                onCancel={toggleMapModal}
                footer={null}
                width={"100%"}
                style={{ top: 20 }}
            >
                <MapView ref={mapRef} products={data} />
            </Modal>
        </div>
    )
}

export default AdminDashboard
